// MUI
import {
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Button,
} from '@mui/material'

// Components
import Header from 'components/molecules/Header/Header'
import AlertDialog from 'components/AlertDialog/AlertDialog'
import Select from 'components/molecules/Select/Select'
import { TabPanel } from 'components/molecules/TablPanel/TabPanel'
import { DataTable } from 'components/molecules/DataTable/DataTable'
import { AssignCardModal } from './AssignCardModal/AssignCardModal'
import { DateRangePickerV2 } from 'components/molecules/DateRangePickerV2/DateRangePickerV2'

// Types
import { SurgeryData } from 'lib/services/api/reports-service/preference-card/getProcedures/types'

// Logic
import useLogic from './PreferenceCards.logic'

// Style
import styles from './PreferenceCards.module.scss'

const PreferenceCards = () => {
  const {
    page,
    isError,
    afterDate,
    beforeDate,
    totalCount,
    rowsPerPage,
    searchName,
    isAlertOpen,
    searchSurgeon,
    proceduresData,
    procedures,
    isLoadingAction,
    isLoadingSearch,
    isErrorPrefCard,
    surgeonsOptions,
    searchProcedure,
    preferenceCards,
    selectedSurgery,
    prefCardHeadCells,
    isAssignModalOpen,
    isLoadingSurgeons,
    isLoadingPrefCards,
    isLoadingProcedures,
    proceduresHeadCells,
    procedureTypesOptions,
    isLoadingProcedureTypes,
    selectedProcedureRows,
    selectedPrefCardRows,
    activeTab,
    showPascPreferenceCardFeatures,
    setSearchName,
    setIsAlertOpen,
    setSearchSurgeon,
    handlePageChange,
    setActiveTab,
    handleAlertAction,
    setSearchProcedure,
    handleCreatePrefCard,
    handleCloseAssignModal,
    handleRowsPerPageChange,
    handleRowSelect,
    setSelectedProcedureRows,
    setSelectedProcedureData,
    setSelectedPrefCardRows,
    setSelectedPrefCardData,
    handleExportAll,
    handleExportSelected,
    handleDateRangeChange,
    handleExportTodaySurgeries,
    handleExportTomorrowSurgeries,
    handleExportDateRange,
    isExportingToday,
    isExportingTomorrow,
    isExportingDateRange,
    exportAllPrefCardsState,
    isGeneratingPDF,
  } = useLogic()

  return (
    <div className={styles.container}>
      <Header
        parent="Reports"
        title="Procedures"
        actions={[
          {
            onClick: () => handleCreatePrefCard(false),
            title: 'Create',
            color: 'primary',
            variant: 'contained',
            size: 'medium',
          },
        ]}
      />

      <TabPanel
        customTabPanelSx={{
          p: 0,
          mt: 0,
        }}
        value={activeTab}
        onChange={setActiveTab}
        tabsConfig={[
          {
            label: 'Scheduled Procedures',
            content: (
              <div className={styles.surgeries}>
                <Box
                  aria-label="date-picker-box"
                  className={styles.datePickerBox}
                >
                  <DateRangePickerV2
                    startDate={afterDate}
                    endDate={beforeDate}
                    onChange={handleDateRangeChange}
                  />
                  {showPascPreferenceCardFeatures && (
                    <Box className={styles.exportButtons}>
                      <Button
                        variant="contained"
                        size="small"
                        className={styles.exportButton}
                        onClick={handleExportTodaySurgeries}
                      >
                        {isExportingToday ? 'Exporting...' : "Export Today's"}
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        className={styles.exportButton}
                        onClick={handleExportTomorrowSurgeries}
                      >
                        {isExportingTomorrow
                          ? 'Exporting...'
                          : "Export Tomorrow's"}
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        className={styles.exportButton}
                        onClick={handleExportDateRange}
                      >
                        {isExportingDateRange
                          ? 'Exporting...'
                          : 'Export date Range'}
                      </Button>
                    </Box>
                  )}
                </Box>
                <Box className={styles.tableContainer}>
                  <DataTable
                    data-testid="procedures-table"
                    tableHeaders={proceduresHeadCells}
                    tableRows={proceduresData || []}
                    isLoading={isLoadingProcedures}
                    isErrored={isError}
                    initialOrder="desc"
                    pagination={true}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalCount={totalCount}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    enableCheckboxes={showPascPreferenceCardFeatures}
                    selectedRows={selectedProcedureRows}
                    onRowSelect={handleRowSelect}
                    onExportSelected={handleExportSelected}
                    onClearSelection={() => {
                      setSelectedProcedureRows([])
                      setSelectedProcedureData([])
                    }}
                    showExportButtons={showPascPreferenceCardFeatures}
                    isExporting={
                      isExportingToday ||
                      isExportingTomorrow ||
                      isExportingDateRange ||
                      isGeneratingPDF
                    }
                    disabledCheckboxIds={procedures?.surgeries
                      .filter((surgery) => surgery.preferenceCardId === null)
                      .map((surgery) => surgery.surgeryId)}
                  />
                </Box>
              </div>
            ),
          },
          {
            label: 'Preference Cards',
            content: (
              <div className={styles.prefCards}>
                <div className={styles.filters}>
                  <div className={styles.search}>
                    <div className={`${styles.input} ${styles.fullWidth}`}>
                      <TextField
                        InputProps={{
                          endAdornment: isLoadingSearch ? (
                            <InputAdornment position="start">
                              <CircularProgress size={25} />
                            </InputAdornment>
                          ) : null,
                        }}
                        placeholder="Search"
                        InputLabelProps={{ shrink: true }}
                        label="Search card name"
                        value={searchName}
                        onChange={(e) => setSearchName(e.target.value)}
                        fullWidth
                      />
                    </div>

                    <div className={styles.input}>
                      <Select
                        isLoading={isLoadingSurgeons}
                        label="Search surgeon"
                        options={surgeonsOptions ?? []}
                        onSelect={(value) => setSearchSurgeon(value.name)}
                        selectedValue={(searchSurgeon as string) ?? ''}
                        fullWidth
                        onClear={() => setSearchSurgeon(undefined)}
                      />
                    </div>
                    <div className={styles.input}>
                      <Select
                        isLoading={isLoadingProcedureTypes}
                        label="Search procedure"
                        fullWidth
                        options={procedureTypesOptions ?? []}
                        onSelect={(value) => setSearchProcedure(value.name)}
                        selectedValue={searchProcedure}
                        onClear={() => setSearchProcedure(undefined)}
                      />
                    </div>
                  </div>
                </div>
                <Box className={styles.tableContainer}>
                  <DataTable
                    data-testid="preference-cards-table"
                    tableHeaders={prefCardHeadCells}
                    tableRows={preferenceCards || []}
                    isLoading={isLoadingPrefCards}
                    isErrored={isErrorPrefCard}
                    pagination={true}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalCount={totalCount}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    enableCheckboxes={showPascPreferenceCardFeatures}
                    showExportButtons={showPascPreferenceCardFeatures}
                    selectedRows={selectedPrefCardRows}
                    onRowSelect={handleRowSelect}
                    onExportAll={handleExportAll}
                    onExportSelected={handleExportSelected}
                    onClearSelection={() => {
                      setSelectedPrefCardRows([])
                      setSelectedPrefCardData([])
                    }}
                    isExporting={exportAllPrefCardsState.all || isGeneratingPDF}
                  />
                </Box>
              </div>
            ),
          },
        ]}
      />

      <AssignCardModal
        open={isAssignModalOpen}
        data={selectedSurgery ?? ({} as SurgeryData)}
        onClose={handleCloseAssignModal}
        onCreateNew={() =>
          handleCreatePrefCard(
            true,
            selectedSurgery?.surgeonId,
            selectedSurgery?.procedures[0]?.description
          )
        }
        isLoading={isLoadingPrefCards}
      />

      <AlertDialog
        key={isAlertOpen.id}
        zIndex={1000}
        mode="warning"
        isOpen={isAlertOpen.isOpen}
        title={
          isAlertOpen.type === 'delete'
            ? 'Delete Confirmation'
            : 'Unassign Confirmation'
        }
        description={
          isAlertOpen.type === 'delete'
            ? 'Are you sure you want to delete this preference card?'
            : 'Are you sure you want to unassign this preference card from this surgery?'
        }
        isPrimaryLoading={isLoadingAction}
        primaryButtonText={
          isAlertOpen.type === 'delete' ? 'Delete' : 'Unassign'
        }
        primaryButtonAction={handleAlertAction}
        secondaryButtonAction={() =>
          setIsAlertOpen({ isOpen: false, type: 'delete' })
        }
      />
    </div>
  )
}

export default PreferenceCards
